import { Fragment, type ReactNode } from "react";

import { Button, EmptyState, LoadingBlock, Alert } from "./ui";

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  sortable?: boolean;
  className?: string;
}

export interface DataTableProps<T> {
  caption: string;
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: string | null;
  emptyTitle?: string;
  emptyBody?: ReactNode;
  sort?: { key: string; dir: "asc" | "desc" };
  onSort?: (key: string) => void;
  page?: number;
  pageSize?: number;
  total?: number;
  onPage?: (page: number) => void;
  /** Optional section heading per row; a heading row is shown whenever it changes. */
  groupBy?: (row: T) => string;
}

/** Accessible, responsive table with sorting and pagination controls. */
export function DataTable<T>(props: DataTableProps<T>) {
  const { caption, columns, rows, rowKey, loading, error, sort, onSort, groupBy } = props;
  if (loading) return <LoadingBlock />;
  if (error)
    return (
      <Alert tone="error" title="We could not load this list.">
        {error}
      </Alert>
    );
  if (!rows || rows.length === 0)
    return (
      <EmptyState title={props.emptyTitle ?? "Nothing here yet"}>{props.emptyBody}</EmptyState>
    );

  return (
    <div>
      <div className="overflow-x-auto rounded-lg border border-line bg-surface">
        <table className="min-w-full divide-y divide-line text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead className="bg-canvas">
            <tr>
              {columns.map((c) => {
                const active = sort?.key === c.key;
                const ariaSort = active
                  ? sort?.dir === "asc"
                    ? "ascending"
                    : "descending"
                  : undefined;
                return (
                  <th
                    key={c.key}
                    scope="col"
                    aria-sort={ariaSort}
                    className={`px-4 py-3 text-left font-semibold ${c.className ?? ""}`}
                  >
                    {c.sortable && onSort ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 hover:underline"
                        onClick={() => onSort(c.key)}
                      >
                        {c.header}
                        <span aria-hidden="true">
                          {active ? (sort?.dir === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((row, i) => {
              const group = groupBy?.(row);
              const showGroup =
                group !== undefined && (i === 0 || groupBy?.(rows[i - 1]!) !== group);
              const count = showGroup ? rows.filter((r) => groupBy?.(r) === group).length : 0;
              return (
                <Fragment key={rowKey(row)}>
                  {showGroup && (
                    <tr className="bg-brand-50">
                      <th
                        scope="colgroup"
                        colSpan={columns.length}
                        className="px-4 py-2 text-left font-bold text-brand-800"
                      >
                        {group}{" "}
                        <span className="font-normal text-ink-muted">
                          � {count} order{count === 1 ? "" : "s"}
                        </span>
                      </th>
                    </tr>
                  )}
                  <tr className="hover:bg-brand-50/50">
                    {columns.map((c) => (
                      <td key={c.key} className={`px-4 py-3 align-top ${c.className ?? ""}`}>
                        {c.render(row)}
                      </td>
                    ))}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination
        page={props.page}
        pageSize={props.pageSize}
        total={props.total}
        onPage={props.onPage}
      />
    </div>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page?: number;
  pageSize?: number;
  total?: number;
  onPage?: (p: number) => void;
}) {
  if (!page || !pageSize || total === undefined || !onPage) return null;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return <p className="mt-3 text-sm text-ink-muted">{total} total</p>;
  return (
    <nav aria-label="Pagination" className="mt-3 flex items-center justify-between gap-2 text-sm">
      <span className="text-ink-muted">
        Page {page} of {pages} ({total} total)
      </span>
      <span className="flex gap-2">
        <Button variant="secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <Button variant="secondary" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          Next
        </Button>
      </span>
    </nav>
  );
}
